import { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo, ReactNode } from 'react';
import { authService, AuthUser, AuthProvider as AuthProviderType } from '../lib/auth';
import { authenticatedFetch, authenticatedJSONFetch } from '../lib/api';
import { UserReading } from '../types';

// ============================================================================
// 请求去重装饰器
// ============================================================================
class RequestDeduplicator {
  private pending = new Map<string, Promise<any>>();
  
  async deduplicate<T>(key: string, fn: () => Promise<T>): Promise<T> {
    if (this.pending.has(key)) {
      console.log(`🔄 [Dedup] Reusing pending request: ${key}`);
      return this.pending.get(key)!;
    }
    
    const promise = fn().finally(() => {
      this.pending.delete(key);
    });
    
    this.pending.set(key, promise);
    return promise;
  }
}

// ============================================================================
// 短 TTL 缓存
// ============================================================================
class UserCache {
  private cache: AuthUser | null = null;
  private timestamp: number = 0;
  private TTL = 30000; // 30 秒
  
  get(): AuthUser | null {
    if (!this.cache || Date.now() - this.timestamp > this.TTL) {
      return null;
    }
    console.log('✅ [Cache] Using cached user');
    return this.cache;
  }
  
  set(user: AuthUser | null) {
    this.cache = user;
    this.timestamp = Date.now();
  }
  
  invalidate() {
    console.log('🗑️ [Cache] Invalidating cache');
    this.cache = null;
    this.timestamp = 0;
  }
}

// ============================================================================
// Context 类型定义
// ============================================================================
interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  credits: number;

  // 默认查询
  defaultReading: UserReading | null;
  defaultReadingLoading: boolean;

  // 认证方法
  signUp: (email: string, password: string, name?: string) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<any>;
  signInWithProvider: (provider: AuthProviderType) => Promise<any>;
  updateProfile: (updates: Partial<AuthUser>) => Promise<any>;
  refreshUser: (force?: boolean) => Promise<void>;

  // 积分方法
  hasEnoughCredits: (amount: number) => boolean;
  consumeCredits: (amount: number, type?: string, referenceId?: string, description?: string) => Promise<any>;

  // 默认查询方法
  refreshDefaultReading: () => Promise<void>;

  // 认证请求方法
  authenticatedFetch: (url: string, options?: RequestInit) => Promise<Response>;
  authenticatedJSONFetch: <T = any>(url: string, options?: RequestInit) => Promise<{ ok: boolean; data?: T; error?: any }>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// ============================================================================
// AuthProvider 组件
// ============================================================================
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 默认查询状态
  const [defaultReading, setDefaultReading] = useState<UserReading | null>(null);
  const [defaultReadingLoading, setDefaultReadingLoading] = useState(false);

  const deduplicator = useRef(new RequestDeduplicator());
  const cache = useRef(new UserCache());
  const subscriptionRef = useRef<{ unsubscribe: () => void } | null>(null);
  
  // ============================================================================
  // 核心方法: 获取用户 (带去重和缓存)
  // ============================================================================
  const fetchUser = useCallback(async (force = false) => {
    // 检查缓存
    if (!force) {
      const cached = cache.current.get();
      if (cached) {
        setUser(cached);
        setLoading(false);
        return cached;
      }
    }
    
    // 请求去重
    return deduplicator.current.deduplicate('getCurrentUser', async () => {
      console.log('🔄 [AuthProvider] Fetching user from API');
      setLoading(true);
      
      try {
        const authUser = await authService.getCurrentUser();
        
        // 更新缓存和状态
        cache.current.set(authUser);
        setUser(authUser);
        setError(null);
        
        return authUser;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch user';
        console.warn('⚠️ [AuthProvider] Failed to fetch user:', errorMessage);
        setError(errorMessage);
        setUser(null);
        throw err;
      } finally {
        setLoading(false);
      }
    });
  }, []);
  
  // ============================================================================
  // refreshUser: 失效缓存并重新获取
  // ============================================================================
  const refreshUser = useCallback(async (force = false) => {
    if (force) {
      cache.current.invalidate();
    }
    return fetchUser(true);
  }, [fetchUser]);

  // ============================================================================
  // 获取默认查询
  // ============================================================================
  const fetchDefaultReading = useCallback(async () => {
    if (!user) {
      setDefaultReading(null);
      return;
    }

    setDefaultReadingLoading(true);

    try {
      console.log('🔄 [AuthProvider] Fetching default reading');
      const response = await authenticatedJSONFetch<{ reading: UserReading | null; has_default: boolean }>('/api/readings/default');

      if (response.ok && response.data) {
        // 适配新的 API 响应格式：{ reading: UserReading | null, has_default: boolean }
        if (response.data.has_default && response.data.reading) {
          console.log('✅ [AuthProvider] Default reading loaded:', response.data.reading.id);
          setDefaultReading(response.data.reading);
        } else {
          console.log('ℹ️ [AuthProvider] No default reading found');
          setDefaultReading(null);
        }
      } else {
        console.log('ℹ️ [AuthProvider] No default reading found');
        setDefaultReading(null);
      }
    } catch (err) {
      console.warn('⚠️ [AuthProvider] Failed to fetch default reading:', err);
      setDefaultReading(null);
    } finally {
      setDefaultReadingLoading(false);
    }
  }, [user]);

  // ============================================================================
  // refreshDefaultReading: 刷新默认查询
  // ============================================================================
  const refreshDefaultReading = useCallback(async () => {
    return fetchDefaultReading();
  }, [fetchDefaultReading]);
  
  // ============================================================================
  // 初始化: 只执行一次
  // ============================================================================
  useEffect(() => {
    let isMounted = true;

    const initAuth = async () => {
      try {
        // 获取初始用户状态
        await fetchUser();

        // 设置认证状态监听 (只订阅一次)
        const { data } = await authService.onAuthStateChange(async (event, session) => {
          if (!isMounted) return;

          console.log(`🔄 [AuthProvider] Auth state changed: ${event}`);

          if (event === 'SIGNED_OUT' || !session) {
            cache.current.invalidate();
            setUser(null);
            setDefaultReading(null);
          } else if (event === 'SIGNED_IN') {
            // 登录时刷新用户信息
            setTimeout(() => {
              if (isMounted) {
                refreshUser(true);
              }
            }, 100);
          }
          // TOKEN_REFRESHED 和 INITIAL_SESSION 不触发重新获取
        });

        subscriptionRef.current = data.subscription;
      } catch (err) {
        console.error('❌ [AuthProvider] Failed to initialize auth:', err);
        setUser(null);
        setLoading(false);
      }
    };

    initAuth();

    return () => {
      isMounted = false;
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
      }
    };
  }, [fetchUser, refreshUser]);

  // ============================================================================
  // 用户变化时自动获取默认查询
  // ============================================================================
  useEffect(() => {
    if (user && !loading) {
      console.log('👤 [AuthProvider] User authenticated, fetching default reading');
      fetchDefaultReading();
    } else if (!user) {
      setDefaultReading(null);
    }
  }, [user, loading, fetchDefaultReading]);

  // ============================================================================
  // 认证方法
  // ============================================================================
  const signUp = useCallback(async (email: string, password: string, name?: string) => {
    setLoading(true);
    setError(null);

    try {
      console.log('📝 Starting sign up process...');
      const { data, error: signUpError } = await authService.signUp(email, password, name);

      if (signUpError) {
        console.error('❌ Sign up failed:', signUpError);
        setError(signUpError);
        setLoading(false);
        return { success: false, error: signUpError };
      }

      console.log('✅ Supabase sign up successful');

      // 检查是否需要邮箱验证
      if (data?.user && !data?.session) {
        console.log('📧 Email verification required - no session created');
        setLoading(false);
        return {
          success: false,
          requiresVerification: true,
          email: data.user.email || email,
          error: null
        };
      }

      // 如果有 session，说明可以直接登录
      if (data?.user && data?.session) {
        console.log('✅ Session created, attempting to get user info...');

        try {
          const userInfo = await fetchUser(true);
          if (userInfo) {
            console.log('✅ User info retrieved successfully after signup:', userInfo);
          }
        } catch (err) {
          console.warn('⚠️ Failed to get user info after signup:', err);
          // 设置基础用户信息
          setUser({
            id: data.user.id,
            email: data.user.email || '',
            name: name || '',
            avatar_url: data.user.user_metadata?.avatar_url,
            credits: 1,
            language_preference: 'zh',
            subscription_status: 'free',
            total_readings: 0
          });
        }

        setLoading(false);
        console.log('🎉 Sign up process completed successfully with session');
        return { success: true, data };
      }

      setLoading(false);
      console.warn('⚠️ Unexpected signup state - no session and no verification required');
      return { success: false, error: '注册状态异常，请重试' };

    } catch (err: unknown) {
      console.error('❌ Sign up process error:', err);
      const errorMessage = err instanceof Error ? err.message : '注册过程中发生错误';
      setError(errorMessage);
      setLoading(false);
      return { success: false, error: errorMessage };
    }
  }, [fetchUser]);

  const signIn = useCallback(async (email: string, password: string) => {
    setLoading(true);
    setError(null);

    try {
      console.log('🔐 Starting sign in process...');
      const { data, error: signInError } = await authService.signIn(email, password);

      if (signInError) {
        console.error('❌ Sign in failed:', signInError);
        setError(signInError);
        setLoading(false);
        return { success: false, error: signInError };
      }

      console.log('✅ Supabase sign in successful, getting user info...');

      // 登录成功后获取用户信息，增加重试机制
      if (data?.user) {
        let userInfo = null;
        let retryCount = 0;
        const maxRetries = 3;

        while (!userInfo && retryCount < maxRetries) {
          try {
            console.log(`🔄 Attempting to get user info (attempt ${retryCount + 1}/${maxRetries})`);
            userInfo = await fetchUser(true);

            if (userInfo && userInfo.email) {
              console.log('✅ User info retrieved successfully:', userInfo);
              break;
            }
          } catch (err) {
            console.warn(`⚠️ Failed to get user info (attempt ${retryCount + 1}):`, err);
          }

          retryCount++;
          if (retryCount < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        }

        if (!userInfo) {
          console.error('❌ Failed to get user info after all retries');
          setError('登录成功但无法获取用户信息，请重试');
          setLoading(false);
          return { success: false, error: '无法获取用户信息' };
        }
      }

      setLoading(false);
      console.log('🎉 Sign in process completed successfully');
      return { success: true, data };
    } catch (err: unknown) {
      console.error('❌ Sign in process error:', err);
      const errorMessage = err instanceof Error ? err.message : '登录过程中发生错误';
      setError(errorMessage);
      setLoading(false);
      return { success: false, error: errorMessage };
    }
  }, [fetchUser]);

  const signOut = useCallback(async () => {
    setLoading(true);
    const { error: signOutError } = await authService.signOut();

    if (signOutError) {
      setError(signOutError);
      setLoading(false);
      return { success: false, error: signOutError };
    }

    cache.current.invalidate();
    setUser(null);
    setDefaultReading(null);
    return { success: true };
  }, []);

  const signInWithProvider = useCallback(async (provider: AuthProviderType) => {
    setLoading(true);
    setError(null);

    const { data, error: providerError } = await authService.signInWithProvider(provider);

    if (providerError) {
      setError(providerError);
      setLoading(false);
      return { success: false, error: providerError };
    }

    // OAuth redirect will handle the rest
    return { success: true, data };
  }, []);

  const updateProfile = useCallback(async (updates: Partial<AuthUser>) => {
    if (!user) return { success: false, error: 'No user logged in' };

    const { data, error: updateError } = await authService.updateProfile(updates);

    if (updateError) {
      setError(updateError);
      return { success: false, error: updateError };
    }

    if (data) {
      const updatedUser = { ...user, ...updates };
      setUser(updatedUser);
      cache.current.set(updatedUser);
    }

    return { success: true, data };
  }, [user]);

  // ============================================================================
  // 积分方法
  // ============================================================================
  const hasEnoughCredits = useCallback((amount: number) => {
    return (user?.credits || 0) >= amount;
  }, [user?.credits]);

  const consumeCredits = useCallback(async (
    amount: number,
    type: string = 'reading',
    referenceId?: string,
    description?: string
  ) => {
    if (!user) return { success: false, error: 'User not authenticated' };

    setLoading(true);

    try {
      const supabase = await authService['getSupabase']();
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No authenticated session');

      const response = await fetch('/api/credits/consume', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          amount,
          description: description || `${type} consumption`
        })
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error?.message || 'Failed to consume credits');
      }

      // 优先使用服务端返回的新余额
      const newBalance: number | undefined = result.data?.newBalance ?? result.newBalance;
      const balanceAfter = typeof newBalance === 'number' ? newBalance : (user.credits - amount);

      // 更新本地用户状态和缓存
      const updatedUser = {
        ...user,
        credits: balanceAfter
      };
      setUser(updatedUser);
      cache.current.set(updatedUser);
      setLoading(false);

      return {
        success: true,
        newBalance: balanceAfter,
        transaction: {
          id: Date.now().toString(),
          user_id: user.id,
          type: 'consume',
          amount: -amount,
          description: description || `${type} consumption`,
          created_at: new Date().toISOString(),
          balance_before: user.credits,
          balance_after: balanceAfter,
          status: 'completed'
        }
      };
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setError(errorMessage);
      setLoading(false);
      return {
        success: false,
        error: errorMessage
      };
    }
  }, [user]);

  // ============================================================================
  // 认证请求方法
  // ============================================================================
  const authenticatedRequest = useCallback(async (
    url: string,
    options?: RequestInit
  ): Promise<Response> => {
    if (!user) {
      throw new Error('User not authenticated');
    }
    return authenticatedFetch(url, options);
  }, [user]);

  const authenticatedJSONRequest = useCallback(async <T = any>(
    url: string,
    options?: RequestInit
  ): Promise<{ ok: boolean; data?: T; error?: any }> => {
    if (!user) {
      return { ok: false, error: 'User not authenticated' };
    }
    return authenticatedJSONFetch<T>(url, options);
  }, [user]);

  // ============================================================================
  // 稳定化 user 对象，防止无限 re-render
  // ============================================================================
  const stableUser = useMemo(() => user, [
    user?.id,
    user?.email,
    user?.name,
    user?.credits,
    user?.subscription_status,
    user?.total_readings
  ]);

  const stableAuthState = useMemo(() => ({
    loading,
    error,
    isAuthenticated: !!user
  }), [loading, error, user]);

  // ============================================================================
  // Context Value
  // ============================================================================
  const value: AuthContextValue = {
    user: stableUser,
    loading: stableAuthState.loading,
    error: stableAuthState.error,
    isAuthenticated: stableAuthState.isAuthenticated,
    credits: stableUser?.credits || 0,

    // 默认查询
    defaultReading,
    defaultReadingLoading,

    // 认证方法
    signUp,
    signIn,
    signOut,
    signInWithProvider,
    updateProfile,
    refreshUser,

    // 积分方法
    hasEnoughCredits,
    consumeCredits,

    // 默认查询方法
    refreshDefaultReading,

    // 认证请求方法
    authenticatedFetch: authenticatedRequest,
    authenticatedJSONFetch: authenticatedJSONRequest
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// ============================================================================
// useAuth Hook
// ============================================================================
export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }

  return context;
}

